import React, { useState } from "react";
import WidgetCard from "./WidgetCard";
import { Badge, Skeleton } from "antd";
import NoDataText from "./NoDataText";
import Delta from "./Delta";
import { formatMetric } from "@ts/revstack/helpers";
import { REVSTACK_MEASURES } from "@ts/constants";
import RevstackLaggardsSectionWidget from "./RevstackLaggardsSectionWidget";

// Common styles
const commonStyles = {
    row: {
        color: "#1e1b39",
        fontSize: 14,
        fontWeight: 500,
    },
    value: {
        color: "#1e1b39",
        fontSize: 16,
        fontWeight: 600,
    },
    change: {
        fontSize: 12,
        fontWeight: 600,
    },
    badge: {
        width: 71.96,
        height: 35,
        borderRadius: "6px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    laggardHeader: {
        color: "#1e1b39",
        backgroundColor: "#F0F4F8",
        fontSize: 14,
    },
};

// Common row wrapper component
const RowWrapper = ({
    children,
    isLast,
    className = "",
    style = {},
}: {
    children: React.ReactNode;
    isLast: boolean;
    className?: string;
    style?: React.CSSProperties;
}) => (
    <div
        className={`row align-items-center bg-white position-relative mb-0 py-2 ${
            !isLast ? "border-bottom" : ""
        } ${className}`}
        style={style}
    >
        {children}
    </div>
);

// Common loading skeleton component
const LoadingSkeleton = ({
    height,
    className = "",
    style = {},
}: {
    height: number;
    className?: string;
    style?: React.CSSProperties;
}) => (
    <Skeleton.Node className={className} active style={{ height, ...style }} />
);

const HeaderSection = ({
    title,
    icon,
    status,
    statusColor,
    metricsLoading,
}: any) => (
    <div className="d-flex justify-content-between align-items-center p-4">
        <div className="d-flex align-items-center gap-2">
            <div className="icon-btn">{icon}</div>
            <h5 className="mb-0">{title}</h5>
        </div>
        {metricsLoading ? (
            <LoadingSkeleton height={35} style={{ width: 71.96 }} />
        ) : (
            <Badge
                count={status}
                className="fw-semibold text-white"
                style={{
                    ...commonStyles.badge,
                    backgroundColor: statusColor,
                }}
            />
        )}
    </div>
);

const MetricRow = ({
    metric,
    isLast,
}: {
    metric: any | null;
    isLast: boolean;
}) => {
    if (!metric) return null;

    const hasChange = !isNaN(metric?.change);

    let isPositive = hasChange && +metric?.change ? +metric?.change >= 0 : null;

    // Check for metrics which have inverse change
    if (
        hasChange &&
        REVSTACK_MEASURES[metric?.key] &&
        REVSTACK_MEASURES[metric?.key].invertChangeSign
    ) {
        isPositive = metric?.change ? +metric?.change < 0 : null;
    }

    const deltaIsPositive = hasChange && +metric?.change ? metric?.change >= 0 : null;
    
    const hasValue =
        metric.value !== undefined &&
        metric.value !== null &&
        metric.value !== "" &&
        metric?.change !== "-";

    return (
        <RowWrapper isLast={isLast}>
            <div className="col-5 px-0" style={commonStyles.row}>
                <span
                    className={`text-start ${
                        hasChange && !isPositive ? "ps-2" : ""
                    }`}
                    style={{
                        ...(hasChange && !isPositive
                            ? {
                                  borderLeft: "4px solid #F04438",
                                  fontWeight: 600,
                              }
                            : { fontWeight: 500 }),
                    }}
                >
                    {metric.name}
                </span>
            </div>
            <div className="col-3 text-end px-0" style={commonStyles.value}>
                {formatMetric(metric.key, metric.value)}
            </div>
            <div
                className={`col-4 text-end d-flex align-items-center justify-content-end px-0 ${
                    hasChange
                        ? isPositive
                            ? "text-success"
                            : "text-danger"
                        : ""
                }`}
                style={commonStyles.change}
            >
                {hasChange ? (
                    <span className="d-flex align-items-center justify-content-end gap-1">
                        <Delta isPositive={deltaIsPositive} />
                        <span>{formatMetric(metric.key, metric.change, true)}</span>
                    </span>
                ) : (
                    "-"
                )}
            </div>
        </RowWrapper>
    );
};


const RevstackSummaryTableCard: React.FC<any> = ({
    title,
    icon,
    metricsData,
    config,
    drilldownData,
    isMetricsLoading,
    isDrilldownLoading,
}) => {
    const defaultFilterOption = config?.defaultFilterOption?.value || "";
    const [selectedFilter, setSelectedFilter] = useState<string>(defaultFilterOption);

    const hasValidMetrics =
        metricsData.metrics &&
        metricsData.metrics.length > 0 &&
        metricsData.metrics.some(
            (metric: any) =>
                metric.value !== undefined &&
                metric.value !== null &&
                metric.value !== ""
        );

    const containerHeight = 125;

    const renderMetrics = () => {
        if (!hasValidMetrics) {
            return <NoDataText />;
        }

        return metricsData.metrics.map((metric: any, index: number) => (
            <MetricRow
                key={index}
                metric={metric}
                isLast={index === metricsData.metrics.length - 1}
            />
        ));
    };

    return (
        <WidgetCard className="h-100 w-100" bodyPaddingClass="p-0">
            <HeaderSection
                title={title}
                icon={icon}
                status={metricsData.status}
                statusColor={metricsData.statusColor}
                metricsLoading={isMetricsLoading}
            />
            {isMetricsLoading ? (
                <LoadingSkeleton
                    height={containerHeight}
                    className="px-2 pb-2 w-100"
                />
            ) : (
                <div
                    className={`px-4 mx-2 mb-2 h-100 ${
                        !hasValidMetrics
                            ? "d-flex align-items-center justify-content-center"
                            : ""
                    }`}
                    style={{ height: containerHeight }}
                >
                    {renderMetrics()}
                </div>
            )}
            {config.disableDrillDown !== true && <RevstackLaggardsSectionWidget
                drilldownData={drilldownData}
                filterOptions={config?.filterOptions}
                selectedFilter={selectedFilter}
                setSelectedFilter={setSelectedFilter}
                drilldown={config?.drilldown}
                isLoading={isDrilldownLoading}
                maxRowsInGroup={config?.maxRowsInGroup}
            />}
        </WidgetCard>
    );
};

export default RevstackSummaryTableCard;
